import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DAILY_CATEGORIES, DAILY_ITEM_STATES, DAILY_OUTCOME_META, fmtDate } from './fleetUtils';
import AttachmentGallery from './AttachmentGallery';

const stateMeta = (v) => DAILY_ITEM_STATES.find(s => s.value === v);
const SEV_LABEL = { non_bloquant: 'Non bloquant', potentiellement_bloquant: 'Potentiellement bloquant' };

const AttemptDetail = ({ attempt, isCurrent, onOpenIssue }) => (
  <div className={`border rounded-lg p-3 space-y-2 ${isCurrent ? 'border-green-400 bg-green-50/50' : 'bg-white'}`}
    data-testid={`history-attempt-${attempt.attempt_number}`}>
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="font-semibold">Tentative n°{attempt.attempt_number}</span>
      {isCurrent && (
        <span className="px-2 py-0.5 rounded bg-green-600 text-white font-medium" data-testid="current-valid-badge">
          Contrôle actuellement valable
        </span>
      )}
      {attempt.outcome && DAILY_OUTCOME_META[attempt.outcome] && (
        <span className={`px-2 py-0.5 rounded ${DAILY_OUTCOME_META[attempt.outcome].cls}`}>
          {DAILY_OUTCOME_META[attempt.outcome].label}
        </span>
      )}
      {attempt.status === 'draft' && <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-600">Brouillon non validé</span>}
    </div>
    <p className="text-xs text-gray-600">
      Débuté : {fmtDate(attempt.started_at)}{attempt.validated_at ? ` — Validé : ${fmtDate(attempt.validated_at)}` : ''}
      {' '}par <strong>{attempt.performed_by?.name}</strong> ({attempt.performed_by?.role_label || 'Admin'})
      {attempt.mileage != null && ` — ${attempt.mileage} km`}
    </p>
    {attempt.recheck_reason && (
      <p className="text-xs text-purple-700" data-testid="attempt-recheck-reason">🔁 Raison du re-contrôle : {attempt.recheck_reason}</p>
    )}
    {(attempt.vehicle_status_before || attempt.vehicle_status_after) && (
      <p className="text-xs text-gray-500">Statut véhicule : {attempt.vehicle_status_before || '—'} → {attempt.vehicle_status_after || '—'}</p>
    )}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
      {DAILY_CATEGORIES.map(cat => {
        const r = (attempt.results || {})[cat.key] || {};
        const m = stateMeta(r.state || 'non_controle');
        return (
          <div key={cat.key} className="text-xs flex items-start justify-between gap-2 border rounded px-2 py-1 bg-white">
            <span className="text-gray-700">{cat.label}</span>
            <span className={`px-1.5 py-0.5 rounded shrink-0 ${m ? m.cls : 'bg-gray-200'}`}>{m ? m.label : r.state}</span>
            {r.state === 'probleme' && r.description && (
              <span className="sr-only">{r.description}</span>
            )}
          </div>
        );
      })}
    </div>
    {DAILY_CATEGORIES.filter(c => (attempt.results || {})[c.key]?.state === 'probleme').map(c => {
      const r = attempt.results[c.key];
      return (
        <p key={c.key} className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          ⚠️ {c.label} — {SEV_LABEL[r.severity] || r.severity} : {r.description}
        </p>
      );
    })}
    {attempt.uncontrolled_justification && (
      <p className="text-xs text-gray-600">Justification « Non contrôlé » : {attempt.uncontrolled_justification}</p>
    )}
    {(attempt.issues_detail || []).length > 0 && (
      <div className="space-y-1.5">
        <p className="text-xs font-semibold">Anomalies liées :</p>
        {attempt.issues_detail.map(iss => (
          <div key={iss.issue_id} className="text-xs border rounded p-2 bg-white space-y-1" data-testid={`history-issue-${iss.issue_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>{iss.item_label} — {iss.description}</span>
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded ${iss.is_blocking ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {iss.severity} · {iss.status}
                </span>
                <Button size="sm" variant="outline" className="h-6 text-xs px-2"
                  onClick={() => onOpenIssue(iss.issue_id)} data-testid={`history-open-issue-${iss.issue_id}`}>
                  Voir l'anomalie
                </Button>
              </div>
            </div>
            {(iss.photos || []).length > 0 && <AttachmentGallery photoIds={iss.photos} />}
          </div>
        ))}
      </div>
    )}
  </div>
);

const DailyHistoryView = ({ fixedVehicleId = null, compact = false, sites = [] }) => {
  const [filters, setFilters] = useState({
    date_from: '', date_to: '', plate: '', site_id: '', author: '', outcome: '',
    with_problems: false, with_recheck: false, attente_decision: false,
  });
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = compact ? 10 : 20;

  const buildParams = useCallback((skip) => {
    const p = { limit, skip };
    if (fixedVehicleId) p.vehicle_id = fixedVehicleId;
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters, fixedVehicleId, limit]);

  const load = useCallback(async (skip = 0, append = false) => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/fleet/daily/history`, { params: buildParams(skip), withCredentials: true });
      setData(res.data);
      setItems(prev => append ? [...prev, ...res.data.items] : res.data.items);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    const t = setTimeout(() => load(0, false), 400);
    return () => clearTimeout(t);
  }, [load]);

  const openIssue = (issueId) => {
    searchParams.set('issue', issueId);
    setSearchParams(searchParams, { replace: true });
  };
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  if (error) {
    return <p className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-3" data-testid="daily-history-error">{error}</p>;
  }

  return (
    <div className="space-y-3" data-testid="daily-history-view">
      <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
        <div>
          <Label className="text-xs">Du</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} data-testid="history-filter-from" />
        </div>
        <div>
          <Label className="text-xs">Au</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} data-testid="history-filter-to" />
        </div>
        {!compact && !fixedVehicleId && (
          <>
            <div>
              <Label className="text-xs">Plaque</Label>
              <Input value={filters.plate} onChange={(e) => setF('plate', e.target.value)} placeholder="AB-123-CD" data-testid="history-filter-plate" />
            </div>
            <div>
              <Label className="text-xs">Site</Label>
              <select value={filters.site_id} onChange={(e) => setF('site_id', e.target.value)}
                className="w-full px-2 py-2 border rounded-md bg-white text-sm" data-testid="history-filter-site">
                <option value="">Tous</option>
                {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Auteur</Label>
              <Input value={filters.author} onChange={(e) => setF('author', e.target.value)} placeholder="Nom" data-testid="history-filter-author" />
            </div>
          </>
        )}
        <div>
          <Label className="text-xs">Résultat</Label>
          <select value={filters.outcome} onChange={(e) => setF('outcome', e.target.value)}
            className="w-full px-2 py-2 border rounded-md bg-white text-sm" data-testid="history-filter-outcome">
            <option value="">Tous</option>
            <option value="conforme">Conforme</option>
            <option value="a_surveiller">Problème non bloquant</option>
            <option value="attente_decision">Attente décision sécurité</option>
          </select>
        </div>
      </div>
      {!compact && (
        <div className="flex flex-wrap gap-4 text-xs">
          {[['with_problems', 'Avec problème'], ['with_recheck', 'Avec re-contrôle'], ['attente_decision', 'En attente de décision sécurité']].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={filters[k]} onChange={(e) => setF(k, e.target.checked)} data-testid={`history-filter-${k}`} />
              {lbl}
            </label>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500" data-testid="daily-history-total">{data ? `${data.total} journée(s) de contrôle` : 'Chargement...'}</p>

      <div className="space-y-2">
        {items.map(d => {
          const isOpen = !!expanded[d.daily_check_id];
          const lastAuthor = d.attempts?.length ? d.attempts[d.attempts.length - 1].performed_by : null;
          return (
            <Card key={d.daily_check_id}>
              <CardContent className="p-0">
                <button type="button" className="w-full text-left p-3 flex flex-wrap items-center justify-between gap-2 hover:bg-gray-50"
                  onClick={() => setExpanded(e => ({ ...e, [d.daily_check_id]: !isOpen }))}
                  data-testid={`daily-history-row-${d.daily_check_id}`}>
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div>
                      <p className="text-sm font-medium">
                        {new Date(`${d.date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        <span className="font-mono font-bold ml-2">{d.plate_number}</span>
                      </p>
                      <p className="text-xs text-gray-500">{d.site_name} — {lastAuthor?.name || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {d.latest_outcome && DAILY_OUTCOME_META[d.latest_outcome] && (
                      <span className={`px-2 py-0.5 rounded ${DAILY_OUTCOME_META[d.latest_outcome].cls}`}>
                        {DAILY_OUTCOME_META[d.latest_outcome].label}
                      </span>
                    )}
                    <span className="text-gray-500">{d.problems_count} problème(s)</span>
                    <span className="text-gray-500">{(d.attempts || []).length} tentative(s)</span>
                  </div>
                </button>
                {isOpen && (
                  <div className="p-3 pt-0 space-y-2" data-testid={`daily-history-detail-${d.daily_check_id}`}>
                    {(d.attempts || []).map(a => (
                      <AttemptDetail key={a.attempt_id || a.attempt_number} attempt={a}
                        isCurrent={a.attempt_number === d.current_valid_attempt}
                        onOpenIssue={openIssue} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {!loading && items.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Aucun contrôle quotidien trouvé</p>}
      </div>

      {data && items.length < data.total && (
        <Button variant="outline" className="w-full" disabled={loading}
          onClick={() => load(items.length, true)} data-testid="daily-history-load-more">
          {loading ? 'Chargement...' : `Charger plus (${items.length}/${data.total})`}
        </Button>
      )}
    </div>
  );
};

export default DailyHistoryView;
